import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildAdjustCacheKey, renderAdjustedImageBitmap } from '../utils/imageAdjustRender';
import { hexToRgba } from '../utils/colorUtils';

const ImageElement = ({
  element,
  isSelected,
  isSpacePressed,
  onPointerDown,
  activeStroke,
}) => {
  const source =
    element.bgRemoval?.status === 'done' && element.bgRemoval.processedSrc
      ? element.bgRemoval.processedSrc
      : element.src;

  const [processedUrl, setProcessedUrl] = useState('');
  const renderIdRef = useRef(0);
  const adjust = element.adjust || {};

  const cropRect = element.crop?.enabled
    ? element.crop
    : { x: 0, y: 0, w: 1, h: 1 };

  const eraser = element.eraser || {};
  const activeEraser = activeStroke
    ? { ...eraser, strokes: [...(eraser.strokes || []), activeStroke] }
    : eraser;

  const needsProcessing = useMemo(() => {
    const hasAdjust =
      adjust.brightness !== 1
      || adjust.contrast !== 1
      || adjust.saturation !== 1
      || adjust.warmth !== 0
      || adjust.blur !== 0
      || adjust.temperature !== 0
      || adjust.tint !== 0
      || adjust.highlights !== 0
      || adjust.shadows !== 0
      || adjust.whites !== 0
      || adjust.blacks !== 0
      || adjust.vibrance !== 0
      || adjust.invert
      || adjust.sharpness !== 0
      || adjust.clarity !== 0
      || adjust.vignette !== 0;
    const hasCrop = Boolean(element.crop?.enabled);
    const hasFlip = Boolean(element.flipX || element.flipY);
    const hasEraser = Boolean(activeEraser?.strokes?.length);
    const hasMagic = Boolean(activeEraser?.magic?.ops?.length);
    return hasAdjust || hasCrop || hasFlip || hasEraser || hasMagic;
  }, [
    activeEraser?.magic?.ops?.length,
    activeEraser?.strokes?.length,
    adjust,
    element.crop?.enabled,
    element.flipX,
    element.flipY,
  ]);

  const renderKey = useMemo(
    () =>
      buildAdjustCacheKey({
        assetId: element.assetId,
        src: source,
        crop: cropRect,
        flipX: element.flipX,
        flipY: element.flipY,
        adjust,
        eraser: activeEraser,
        targetWidth: element.w,
        targetHeight: element.h,
        maxSize: 2048,
        fit: element.fit,
      }),
    [
      adjust,
      activeEraser,
      cropRect,
      element.assetId,
      element.fit,
      element.flipX,
      element.flipY,
      element.h,
      element.w,
      source,
    ],
  );

  useEffect(() => {
    if (!needsProcessing) {
      setProcessedUrl('');
      return;
    }
    let cancelled = false;
    const renderId = renderIdRef.current + 1;
    renderIdRef.current = renderId;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = source;
    img.onload = () => {
      if (cancelled || renderIdRef.current !== renderId) return;
      requestAnimationFrame(() => {
        if (cancelled || renderIdRef.current !== renderId) return;
        const result = renderAdjustedImageBitmap({
          imgEl: img,
          elementAdjust: adjust,
          crop: cropRect,
          flipX: element.flipX,
          flipY: element.flipY,
          eraser: activeEraser,
          targetWidth: element.w,
          targetHeight: element.h,
          maxSize: 2048,
          fit: element.fit,
          cacheKey: renderKey,
        });
        if (cancelled || renderIdRef.current !== renderId) return;
        setProcessedUrl(result?.dataUrl || '');
      });
    };
    img.onerror = () => {
      if (cancelled) return;
      setProcessedUrl('');
    };
    return () => {
      cancelled = true;
    };
  }, [activeEraser, adjust, cropRect, element.flipX, element.flipY, element.h, element.w, needsProcessing, renderKey, source]);

  const shadow = element.shadow || {};
  const shadowColor = shadow.color || '#000000';
  const shadowOpacity = shadow.opacity ?? 0;
  const shadowStyle = shadow.enabled
    ? `${shadow.x || 0}px ${shadow.y || 0}px ${shadow.blur || 0}px ${hexToRgba(
        shadowColor,
        shadowOpacity,
      )}`
    : 'none';

  const imageStyle = {
    backgroundImage: `url("${processedUrl || source}")`,
    backgroundSize: '100% 100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div
      data-element-id={element.id}
      className={`studio-element ${isSelected ? 'is-selected' : ''} ${
        element.locked ? 'is-locked' : ''
      }`}
      style={{
        left: element.x,
        top: element.y,
        width: element.w,
        height: element.h,
        transform: `rotate(${element.rotation || 0}deg)`,
        opacity: element.opacity ?? 1,
        zIndex: element.zIndex ?? 1,
        borderRadius: element.radius || 0,
        boxShadow: shadowStyle,
        overflow: element.radius ? 'hidden' : 'visible',
      }}
      onPointerDown={(event) => {
        if (isSpacePressed) return;
        event.stopPropagation();
        onPointerDown(event);
      }}
    >
      <div
        className="studio-image-frame"
        style={{
          borderRadius: element.radius || 0,
        }}
      >
        <div
          className="studio-image-visual"
          style={{
            ...imageStyle,
          }}
          draggable={false}
        />
      </div>
    </div>
  );
};

export default ImageElement;
